import smartSearchDictionary from '../data/smartSearch/smart_search_dictionary_v1_updated.json';
import type {
  ServiceCapabilities,
  ServiceCapabilitiesIndex,
  ServiceCapabilityIntent,
  ServiceCapabilityItem,
  ServiceCapabilityRiskLevel,
} from '../types/firestore';

export const SERVICE_CAPABILITIES_VERSION = 'smart-service-graph-v1' as const;

type DictionaryIntent = ServiceCapabilityIntent;

export interface ProviderCategoryOption {
  id: string;
  domainId: string;
  label: string;
  aliases: string[];
  description?: string;
  defaultIntents: DictionaryIntent[];
  optionalIntents: DictionaryIntent[];
  allowCategoryWideSelection: boolean;
  allowDetailedSelection: boolean;
  allowExclusions: boolean;
  requiresQualification: boolean;
  qualificationType?: string | null;
  riskLevel: ServiceCapabilityRiskLevel;
}

export interface ProviderObjectOption {
  id: string;
  domainId: string;
  categoryId: string;
  label: string;
  pluralLabel?: string;
  aliases: string[];
  priority?: 'high' | 'medium' | 'low' | string;
  defaultIntents: DictionaryIntent[];
  optionalIntents: DictionaryIntent[];
  excludeSelectable: boolean;
  requiresQualification: boolean;
  qualificationType?: string | null;
  riskLevel: ServiceCapabilityRiskLevel;
}

export interface ProviderAdvancedCapabilityOption {
  id: string;
  domainId: string;
  categoryId: string;
  objectId: string;
  label: string;
  profileLabel?: string;
  priority?: 'high' | 'medium' | 'low' | string;
  intent?: DictionaryIntent | null;
  requiresQualification: boolean;
  qualificationType?: string | null;
  riskLevel: ServiceCapabilityRiskLevel;
}

interface DictionaryRecord {
  id?: unknown;
  domainId?: unknown;
  categoryId?: unknown;
  objectId?: unknown;
  label?: unknown;
  pluralLabel?: unknown;
  profileLabel?: unknown;
  aliases?: unknown;
  providerSelectable?: unknown;
  publicVisibleInV1?: unknown;
  adminOnly?: unknown;
  futureCandidate?: unknown;
  allowCategoryWideSelection?: unknown;
  allowDetailedSelection?: unknown;
  allowExclusions?: unknown;
  excludeSelectable?: unknown;
  defaultIntents?: unknown;
  optionalIntents?: unknown;
  allowIntents?: unknown;
  priority?: unknown;
  description?: unknown;
  providerProfileLevel?: unknown;
  selectionMode?: unknown;
  type?: unknown;
  intent?: unknown;
  requiresQualification?: unknown;
  qualificationType?: unknown;
  riskLevel?: unknown;
}

const dictionary = smartSearchDictionary as {
  categories?: DictionaryRecord[];
  objects?: DictionaryRecord[];
  providerCapabilityOptions?: DictionaryRecord[];
};

const INTENT_ORDER: ServiceCapabilityIntent[] = ['N', 'D', 'U'];
const RISK_WEIGHT: Record<ServiceCapabilityRiskLevel, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

const normalizeText = (value: unknown): string => String(value || '').trim();

const normalizeAliases = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((item) => normalizeText(item))
        .filter((item, index, source) => item.length > 0 && source.indexOf(item) === index)
    : [];

const isIntent = (value: unknown): value is ServiceCapabilityIntent =>
  value === 'N' || value === 'U' || value === 'D';

export const normalizeIntentList = (value: unknown): ServiceCapabilityIntent[] => {
  const source = Array.isArray(value) ? value : isIntent(value) ? [value] : [];
  return INTENT_ORDER.filter((intent) => source.includes(intent));
};

const normalizeRiskLevel = (value: unknown): ServiceCapabilityRiskLevel =>
  value === 'high' || value === 'medium' ? value : 'low';

const isProviderVisible = (entry: DictionaryRecord): boolean =>
  entry.providerSelectable === true &&
  entry.publicVisibleInV1 === true &&
  entry.adminOnly !== true &&
  entry.futureCandidate !== true;

const priorityWeight = (value: unknown): number => {
  if (value === 'high') return 0;
  if (value === 'medium') return 1;
  if (value === 'low') return 2;
  return 3;
};

const sortByPriorityAndLabel = <T extends { priority?: unknown; label: string }>(a: T, b: T) => {
  const priorityDiff = priorityWeight(a.priority) - priorityWeight(b.priority);
  if (priorityDiff !== 0) return priorityDiff;
  return a.label.localeCompare(b.label, 'pl');
};

const toCategoryOption = (entry: DictionaryRecord): ProviderCategoryOption | null => {
  const id = normalizeText(entry.id);
  const domainId = normalizeText(entry.domainId);
  const label = normalizeText(entry.label);
  if (!id || !domainId || !label || !isProviderVisible(entry)) return null;

  return {
    id,
    domainId,
    label,
    aliases: normalizeAliases(entry.aliases),
    description: normalizeText(entry.description) || undefined,
    defaultIntents: normalizeIntentList(entry.defaultIntents),
    optionalIntents: normalizeIntentList(entry.optionalIntents),
    allowCategoryWideSelection: entry.allowCategoryWideSelection !== false,
    allowDetailedSelection: entry.allowDetailedSelection !== false,
    allowExclusions: entry.allowExclusions === true,
    requiresQualification: entry.requiresQualification === true,
    qualificationType:
      entry.qualificationType === null ? null : normalizeText(entry.qualificationType) || null,
    riskLevel: normalizeRiskLevel(entry.riskLevel),
  };
};

const toObjectOption = (entry: DictionaryRecord): ProviderObjectOption | null => {
  const id = normalizeText(entry.id);
  const domainId = normalizeText(entry.domainId);
  const categoryId = normalizeText(entry.categoryId);
  const label = normalizeText(entry.label);
  if (!id || !domainId || !categoryId || !label || !isProviderVisible(entry)) return null;

  return {
    id,
    domainId,
    categoryId,
    label,
    pluralLabel: normalizeText(entry.pluralLabel) || undefined,
    aliases: normalizeAliases(entry.aliases),
    priority: normalizeText(entry.priority) || undefined,
    defaultIntents: normalizeIntentList(entry.defaultIntents),
    optionalIntents: normalizeIntentList(entry.optionalIntents),
    excludeSelectable: entry.excludeSelectable === true,
    requiresQualification: entry.requiresQualification === true,
    qualificationType:
      entry.qualificationType === null ? null : normalizeText(entry.qualificationType) || null,
    riskLevel: normalizeRiskLevel(entry.riskLevel),
  };
};

const toAdvancedCapabilityOption = (
  entry: DictionaryRecord
): ProviderAdvancedCapabilityOption | null => {
  const id = normalizeText(entry.id);
  const domainId = normalizeText(entry.domainId);
  const categoryId = normalizeText(entry.categoryId);
  const objectId = normalizeText(entry.objectId);
  const label = normalizeText(entry.label);
  const isAdvanced =
    entry.providerProfileLevel === 'advanced' ||
    entry.selectionMode === 'advanced' ||
    entry.type === 'action';

  if (
    !id ||
    !domainId ||
    !categoryId ||
    !objectId ||
    !label ||
    !isAdvanced ||
    !isProviderVisible(entry)
  ) {
    return null;
  }

  return {
    id,
    domainId,
    categoryId,
    objectId,
    label,
    profileLabel: normalizeText(entry.profileLabel) || undefined,
    priority: normalizeText(entry.priority) || undefined,
    intent: isIntent(entry.intent) ? entry.intent : null,
    requiresQualification: entry.requiresQualification === true,
    qualificationType:
      entry.qualificationType === null ? null : normalizeText(entry.qualificationType) || null,
    riskLevel: normalizeRiskLevel(entry.riskLevel),
  };
};

export const providerCapabilityCategories: ProviderCategoryOption[] = (dictionary.categories || [])
  .map(toCategoryOption)
  .filter((entry): entry is ProviderCategoryOption => Boolean(entry))
  .sort(sortByPriorityAndLabel);

export const providerCapabilityObjects: ProviderObjectOption[] = (dictionary.objects || [])
  .map(toObjectOption)
  .filter((entry): entry is ProviderObjectOption => Boolean(entry))
  .sort(sortByPriorityAndLabel);

export const providerAdvancedCapabilityOptions: ProviderAdvancedCapabilityOption[] = (
  dictionary.providerCapabilityOptions || []
)
  .map(toAdvancedCapabilityOption)
  .filter((entry): entry is ProviderAdvancedCapabilityOption => Boolean(entry))
  .sort(sortByPriorityAndLabel);

export const providerCapabilityCategoryById = new Map(
  providerCapabilityCategories.map((category) => [category.id, category])
);

export const providerCapabilityObjectById = new Map(
  providerCapabilityObjects.map((object) => [object.id, object])
);

export const providerAdvancedCapabilityById = new Map(
  providerAdvancedCapabilityOptions.map((capability) => [capability.id, capability])
);

export const getObjectsForProviderCategory = (categoryId: string): ProviderObjectOption[] =>
  providerCapabilityObjects.filter((object) => object.categoryId === categoryId);

export const getAdvancedCapabilitiesForObjects = (
  objectIds: string[]
): ProviderAdvancedCapabilityOption[] => {
  const objectIdSet = new Set(objectIds);
  return providerAdvancedCapabilityOptions.filter((capability) =>
    objectIdSet.has(capability.objectId)
  );
};

const unique = <T extends string>(value: T[]): T[] =>
  Array.from(new Set(value.filter((item) => item.trim().length > 0))).sort((a, b) =>
    a.localeCompare(b, 'pl')
  );

export const buildServiceCapabilitiesIndex = (
  items: ServiceCapabilityItem[]
): ServiceCapabilitiesIndex => ({
  domainIds: unique(items.map((item) => item.domainId)),
  categoryIds: unique(items.map((item) => item.categoryId)),
  objectIds: unique(
    items.flatMap((item) => item.objectSelections.map((selection) => selection.objectId))
  ),
  intents: INTENT_ORDER.filter((intent) =>
    items.some(
      (item) =>
        item.intents.includes(intent) ||
        item.objectSelections.some((selection) => selection.intents?.includes(intent))
    )
  ),
  advancedCapabilityIds: unique(items.flatMap((item) => item.advancedCapabilityIds)),
});

export const toCanonicalServiceCapabilityItem = (
  item: ServiceCapabilityItem
): ServiceCapabilityItem => {
  const intents = normalizeIntentList(item.intents);
  return {
    ...item,
    intents,
    objectSelections:
      item.mode === 'selectedObjects'
        ? item.objectSelections.map((selection) => ({
            objectId: selection.objectId,
            intents: normalizeIntentList(selection.intents ?? intents),
          }))
        : [],
    excludedObjectIds: item.mode === 'categoryWide' ? [...item.excludedObjectIds] : [],
    advancedCapabilityIds: [...item.advancedCapabilityIds],
  };
};

export const toCanonicalServiceCapabilities = (
  value: Pick<ServiceCapabilities, 'items'> & Partial<ServiceCapabilities>
): ServiceCapabilities => {
  const items = value.items.map(toCanonicalServiceCapabilityItem);
  return {
    version: SERVICE_CAPABILITIES_VERSION,
    updatedAt: value.updatedAt ?? null,
    items,
    index: buildServiceCapabilitiesIndex(items),
  };
};

export const createEmptyServiceCapabilities = (): ServiceCapabilities => ({
  version: SERVICE_CAPABILITIES_VERSION,
  updatedAt: null,
  items: [],
  index: {
    domainIds: [],
    categoryIds: [],
    objectIds: [],
    intents: [],
    advancedCapabilityIds: [],
  },
});

export const withServiceCapabilitiesIndex = (
  value: Pick<ServiceCapabilities, 'items'> & Partial<ServiceCapabilities>
): ServiceCapabilities => toCanonicalServiceCapabilities(value);

export const cloneServiceCapabilities = (
  value: ServiceCapabilities | null | undefined
): ServiceCapabilities | null => {
  if (!value || !Array.isArray(value.items)) return null;
  return withServiceCapabilitiesIndex({
    updatedAt: value.updatedAt ?? null,
    items: value.items.map((item) => ({
      ...item,
      intents: normalizeIntentList(item.intents),
      objectSelections: item.objectSelections.map((selection) => ({
        ...selection,
        intents: normalizeIntentList(selection.intents ?? item.intents),
      })),
      excludedObjectIds: [...item.excludedObjectIds],
      advancedCapabilityIds: [...item.advancedCapabilityIds],
    })),
  });
};

export const getRiskLevel = (
  records: Array<{ riskLevel: ServiceCapabilityRiskLevel | string | undefined }>
): ServiceCapabilityRiskLevel => {
  return records.reduce<ServiceCapabilityRiskLevel>((current, record) => {
    const next = normalizeRiskLevel(record.riskLevel);
    return RISK_WEIGHT[next] > RISK_WEIGHT[current] ? next : current;
  }, 'low');
};

export const getQualificationType = (
  records: Array<{ qualificationType?: string | null; requiresQualification?: boolean }>
): string | null =>
  records.find((record) => record.requiresQualification && record.qualificationType)
    ?.qualificationType || null;

export const formatServiceCapabilityIntents = (intents: ServiceCapabilityIntent[]): string => {
  const labels: Record<ServiceCapabilityIntent, string> = {
    N: 'Naprawa',
    U: 'Usługa',
    D: 'Diagnostyka',
  };
  return INTENT_ORDER.filter((intent) => intents.includes(intent))
    .map((intent) => labels[intent])
    .join(', ');
};

export const validateServiceCapabilities = (value: ServiceCapabilities): string | null => {
  if (!value || value.version !== SERVICE_CAPABILITIES_VERSION || !Array.isArray(value.items)) {
    return 'Nieprawidłowy format zakresu usług.';
  }

  if (value.items.length === 0) {
    return 'Dodaj co najmniej jeden zakres usług.';
  }

  for (const item of value.items) {
    const category = providerCapabilityCategoryById.get(item.categoryId);
    if (!category || category.domainId !== item.domainId) {
      return 'Wybrany obszar nie jest dostępny w publicznym profilu.';
    }

    if (item.mode !== 'categoryWide' && item.mode !== 'selectedObjects') {
      return 'Wybierz dokładność zakresu usług.';
    }

    if (normalizeIntentList(item.intents).length === 0) {
      return `Wybierz typy spraw dla zakresu ${category.label}.`;
    }

    const objectIdsInCategory = new Set(
      getObjectsForProviderCategory(item.categoryId).map((object) => object.id)
    );

    if (item.mode === 'selectedObjects' && item.objectSelections.length === 0) {
      return `Wybierz co najmniej jedną rzecz w zakresie ${category.label}.`;
    }

    for (const selection of item.objectSelections) {
      if (!objectIdsInCategory.has(selection.objectId)) {
        return 'Wybrana rzecz nie należy do wybranego obszaru albo nie jest publiczna.';
      }
      if (selection.intents && normalizeIntentList(selection.intents).length === 0) {
        return 'Override typów spraw przy rzeczy nie może być pusty.';
      }
    }

    for (const objectId of item.excludedObjectIds) {
      if (!objectIdsInCategory.has(objectId)) {
        return 'Wykluczenie nie należy do wybranego obszaru albo nie jest publiczne.';
      }
    }

    for (const capabilityId of item.advancedCapabilityIds) {
      const capability = providerAdvancedCapabilityById.get(capabilityId);
      if (!capability || capability.categoryId !== item.categoryId) {
        return 'Mocna usługa nie należy do wybranego zakresu albo nie jest publiczna.';
      }
    }
  }

  return null;
};
